import type { OnlineVisitGuardChat } from '../apis/onlineVisit';

// استایل شیت (ov-guard-sheet*) در pages/_app.tsx ایمپورت می‌شود —
// pages-router ایمپورت CSS سراسری را فقط از _app قبول می‌کند.

/** آستانهٔ گارد بک‌اند: چتِ بیش از این مقدار ساعت بی‌پاسخ، مسیر را بسته است */
const GUARD_HOURS_THRESHOLD = 2;

/** ساعتِ معوقِ خوانا: زیر یک شبانه‌روز «ساعت»، بعدش «روز» — بدون اعشار (نقد دیزاین ۲۰۲۶-۱۰-۰۷) */
export function formatOverdueLabel(hours: number | null | undefined): string {
  const raw = Number(hours ?? 0);
  const value = Number.isFinite(raw) ? Math.max(0, raw) : 0;
  if (value >= 24) {
    const days = Math.max(1, Math.round(value / 24));
    return `${days.toLocaleString('fa-IR')} روز بی‌پاسخ`;
  }
  const roundedHours = Math.max(1, Math.round(value));
  return `${roundedHours.toLocaleString('fa-IR')} ساعت بی‌پاسخ`;
}

function GuardChevronIcon() {
  return (
    <svg
      className="online-visit-guard-chat-chevron"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

type OnlineVisitGuardChatsProps = {
  chats: OnlineVisitGuardChat[];
  /** شمارندهٔ رسمی بک‌اند — جمع/تفکیکِ متن از روی همین است، نه chats.length */
  openCount: number;
  onChatClick?: (chat: OnlineVisitGuardChat) => void;
};

/** بدنهٔ شیت «فعال‌سازی ممکن نیست» — لیست چت‌های بی‌پاسخ.
 * هر ردیف کامل لینک است (هدف لمسی ≥۴۸px)؛ ساعت‌ها humanize (روز/ساعت)؛
 * بیمارِ بی‌نام «بدون نام» — مارک‌آپ عین OnlineVisitGuardChats نلسون‌فرانت. */
export const OnlineVisitGuardChats = ({ chats, openCount, onChatClick }: OnlineVisitGuardChatsProps) => (
  <div className="online-visit-guard-chats" dir="rtl">
    <p className="online-visit-guard-lead">
      {openCount === 1
        ? 'این چت بیش از ۲ ساعت بی‌پاسخ مانده است. لطفاً ابتدا پاسخ دهید، سپس سوییچ را فعال کنید:'
        : 'این چت‌ها بیش از ۲ ساعت بی‌پاسخ مانده است. لطفاً ابتدا پاسخ دهید، سپس سوییچ را فعال کنید:'}
    </p>
    <ul className="online-visit-guard-chat-list">
      {chats.map(chat => {
        const patientLabel = chat.patient_name ?? 'بدون نام';
        // مقایسهٔ آستانه روی مقدار خام عین مرجع (NaN → fresh)؛ گردکردن فقط داخل formatOverdueLabel
        const overdueRaw = Number(chat.hours_overdue ?? 0);
        const isFresh = !(overdueRaw > GUARD_HOURS_THRESHOLD);
        return (
          <li key={chat.ticket_id}>
            <a
              className="online-visit-guard-chat-row"
              data-overdue-chip={isFresh ? 'fresh' : 'overdue'}
              href={chat.chat_url}
              target="_blank"
              rel="noreferrer"
              aria-label={`رفتن به چت — ${patientLabel}`}
              onClick={() => onChatClick?.(chat)}
            >
              <span className="online-visit-guard-chat-name">{patientLabel}</span>
              <span className="online-visit-guard-chat-meta">
                <span className={`online-visit-guard-chat-chip${isFresh ? ' online-visit-guard-chat-chip--fresh' : ''}`}>
                  {formatOverdueLabel(chat.hours_overdue)}
                </span>
                <GuardChevronIcon />
              </span>
            </a>
          </li>
        );
      })}
    </ul>
  </div>
);
